import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import type { Project } from '../../../services/projectManagement/projectsService';
import { tasksService } from '../../../services/projectManagement/tasksService';
import { dependenciesService } from '../../../services/projectManagement/dependenciesService';
import { pmCustomFieldsApiService } from '../../../services/projectManagement/pmCustomFieldsApiService';
import { pmTemplatesApiService, type PmProjectTemplate } from '../../../services/projectManagement/pmTemplatesApiService';

interface ProjectTemplatesModalProps {
  organizationId: string;
  projects: Project[];
  onClose: () => void;
  onProjectCreated: () => void;
}

const ProjectTemplatesModal: React.FC<ProjectTemplatesModalProps> = ({
  organizationId,
  projects,
  onClose,
  onProjectCreated,
}) => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<PmProjectTemplate[]>([]);

  const [selectedTemplate, setSelectedTemplate] = useState<PmProjectTemplate | null>(null);
  const [instantiateName, setInstantiateName] = useState('');
  const [instantiateDescription, setInstantiateDescription] = useState('');
  const [instantiating, setInstantiating] = useState(false);

  const [sourceProjectId, setSourceProjectId] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const sourceProject = useMemo(
    () => projects.find(p => p.id === sourceProjectId) || null,
    [projects, sourceProjectId]
  );

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await pmTemplatesApiService.list(organizationId);
      if (error) {
        setTemplates([]);
      } else {
        setTemplates(data || []);
      }
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (!selectedTemplate) return;
    setInstantiateName(`${selectedTemplate.name} Project`);
    setInstantiateDescription(selectedTemplate.description || '');
  }, [selectedTemplate?.id]);

  useEffect(() => {
    if (!sourceProject) return;
    if (newTemplateName.trim()) return;
    setNewTemplateName(`${sourceProject.name} Template`);
  }, [sourceProject?.id]);

  const handleDeleteTemplate = async (template: PmProjectTemplate) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;

    const { error } = await pmTemplatesApiService.delete(template.id, organizationId);
    if (error) {
      toast.error(String(error));
      return;
    }

    toast.success('Template deleted');
    if (selectedTemplate?.id === template.id) {
      setSelectedTemplate(null);
    }
    void fetchTemplates();
  };

  const handleSaveTemplate = async () => {
    if (!sourceProjectId) {
      toast.error('Select a project to template');
      return;
    }

    const name = newTemplateName.trim();
    if (!name) {
      toast.error('Template name is required');
      return;
    }

    setSavingTemplate(true);
    try {
      const [tasksRes, depsRes, fieldsRes] = await Promise.all([
        tasksService.list({ organization_id: organizationId, project_id: sourceProjectId }),
        dependenciesService.list({ organization_id: organizationId, project_id: sourceProjectId }),
        pmCustomFieldsApiService.list({ organization_id: organizationId, project_id: sourceProjectId }),
      ]);

      if (tasksRes.error) {
        toast.error('Failed to load project tasks');
        return;
      }
      if (depsRes.error) {
        toast.error('Failed to load project dependencies');
        return;
      }
      if (fieldsRes.error) {
        toast.error('Failed to load project custom fields');
        return;
      }

      const rootTasks = tasksRes.data || [];

      const allTasks = [...rootTasks];
      const seen = new Set<string>(rootTasks.map(t => t.id));
      const queue = [...rootTasks];

      while (queue.length > 0 && allTasks.length < 500) {
        const parent = queue.shift();
        if (!parent) continue;

        const subRes = await tasksService.list({
          organization_id: organizationId,
          project_id: sourceProjectId,
          parent_task_id: parent.id,
        });

        if (subRes.error) {
          toast.error('Failed to load project subtasks');
          return;
        }

        (subRes.data || []).forEach(st => {
          if (seen.has(st.id)) return;
          seen.add(st.id);
          allTasks.push(st);
          queue.push(st);
        });
      }

      const taskKeySet = new Set(allTasks.map(t => t.id));

      const template_data = {
        custom_fields: (fieldsRes.data || []).map(f => ({
          field_name: f.field_name,
          field_key: f.field_key,
          field_type: f.field_type,
          field_options: f.field_options,
          required: f.required,
          default_value: f.default_value,
          position: f.position,
        })),
        default_tasks: allTasks.map(t => ({
          key: t.id,
          parentKey: t.parent_task_id || undefined,
          title: t.title,
          description: t.description,
          status: t.status,
          priority: t.priority,
          start_date: t.start_date,
          due_date: t.due_date,
          estimated_time: t.estimated_time,
          labels: t.labels,
        })),
        dependencies: (depsRes.data || [])
          .filter(d => taskKeySet.has(d.task_id) && taskKeySet.has(d.depends_on_task_id))
          .map(d => ({
            from: d.depends_on_task_id,
            to: d.task_id,
            dependency_type: d.dependency_type,
          })),
      };

      const { data, error } = await pmTemplatesApiService.create({
        organization_id: organizationId,
        name,
        description: newTemplateDescription.trim() || null,
        template_data,
      });

      if (error || !data) {
        toast.error(String(error || 'Failed to create template'));
        return;
      }

      toast.success('Template created');
      setNewTemplateName('');
      setNewTemplateDescription('');
      setSourceProjectId('');
      void fetchTemplates();
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleInstantiate = async () => {
    if (!selectedTemplate) {
      toast.error('Select a template');
      return;
    }

    const project_name = instantiateName.trim();
    if (!project_name) {
      toast.error('Project name is required');
      return;
    }

    setInstantiating(true);
    try {
      const { error } = await pmTemplatesApiService.instantiate(selectedTemplate.id, {
        organization_id: organizationId,
        project_name,
        project_description: instantiateDescription.trim() || null,
      });

      if (error) {
        toast.error(String(error));
        return;
      }

      toast.success('Project created from template');
      onProjectCreated();
    } finally {
      setInstantiating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-5xl rounded-xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Project Templates</h3>
            <div className="mt-1 text-sm text-gray-600">Create reusable templates and spin up projects faster.</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            <div className="rounded-lg border border-gray-200 p-4">
              <div className="text-sm font-semibold text-gray-900 mb-3">Save template from project</div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source project</label>
                  <select
                    value={sourceProjectId}
                    onChange={(e) => setSourceProjectId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={projects.length === 0 || savingTemplate}
                  >
                    <option value="">Select project...</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template name</label>
                  <input
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={savingTemplate}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newTemplateDescription}
                    onChange={(e) => setNewTemplateDescription(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={savingTemplate}
                  />
                </div>

                <button
                  onClick={() => void handleSaveTemplate()}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                  disabled={savingTemplate || projects.length === 0}
                >
                  {savingTemplate ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <div className="text-sm font-semibold text-gray-900 mb-3">Templates</div>

              {loading ? (
                <div className="text-sm text-gray-500">Loading...</div>
              ) : templates.length === 0 ? (
                <div className="text-sm text-gray-500">No templates yet.</div>
              ) : (
                <div className="space-y-2">
                  {templates.map(t => {
                    const selected = selectedTemplate?.id === t.id;
                    return (
                      <div
                        key={t.id}
                        className={`rounded-lg border p-3 ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'} cursor-pointer`}
                        onClick={() => setSelectedTemplate(t)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{t.name}</div>
                            {t.description ? (
                              <div className="mt-1 text-xs text-gray-600 line-clamp-2">{t.description}</div>
                            ) : null}
                            <div className="mt-2 text-xs text-gray-500">
                              Uses: {t.use_count}
                              {t.is_public ? ' · Public' : ' · Org'}
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-2">
                            {user?.id && String(t.created_by) === String(user.id) ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleDeleteTemplate(t);
                                }}
                                className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
                              >
                                Delete
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="text-sm font-semibold text-gray-900 mb-3">Create project from template</div>

            {!selectedTemplate ? (
              <div className="text-sm text-gray-500">Select a template on the left.</div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project name</label>
                  <input
                    value={instantiateName}
                    onChange={(e) => setInstantiateName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={instantiating}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={instantiateDescription}
                    onChange={(e) => setInstantiateDescription(e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    disabled={instantiating}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setSelectedTemplate(null)}
                    className="rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                    disabled={instantiating}
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => void handleInstantiate()}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
                    disabled={instantiating}
                  >
                    {instantiating ? 'Creating...' : 'Create Project'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTemplatesModal;
